import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { authAPI } from '../services/api';
import { messaging, VAPID_KEY } from '../firebase-config';
import { getToken, onMessage } from "firebase/messaging";
import { useToast } from '@chakra-ui/react';

const NotificationContext = createContext();

import { useWebSocket } from './WebSocketContext';
import { useNavigate } from 'react-router-dom'; // Optional if needed

export const useNotifications = () => {
    return useContext(NotificationContext);
};

export const NotificationProvider = ({ children }) => {
    const { user } = useAuth();
    const { lastMessage } = useWebSocket();
    const toast = useToast();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    // Safe Check for Notification API
    const isNotificationSupported = 'Notification' in window;

    const [permissionStatus, setPermissionStatus] = useState(
        isNotificationSupported ? Notification.permission : 'denied'
    );

    // Fetch notifications from API
    const fetchNotifications = useCallback(async () => {
        if (!user) return;

        setIsLoading(true);
        try {
            const response = await authAPI.getNotifications();
            const data = response.data;
            setNotifications(data);
            setUnreadCount(data.filter(n => !n.is_read).length);
        } catch (error) {
            console.error("Failed to fetch notifications:", error);
        } finally {
            setIsLoading(false);
        }
    }, [user]);

    // ── 通知刷新節流器（2026-09-04 尖峰崩潰修正）────────────────────────────
    // 事故成因：後端每次 GPS 更新都會對「全機構」發一則 WebSocket 廣播（Dashboard
    // 靜默更新），而下方的 WS 監聽只要看到 type==='broadcast' 就立刻去打
    // GET /notifications/。尖峰時約有 15 條連線同時在線、廣播每 1～3 秒一次，
    // 於是「1 次 GPS 更新 → 1 次廣播 → 15 支 API 呼叫」的扇出風暴，讓
    // /notifications/ 成為全站流量第一名（4 分鐘 427 次，是第二名的 5 倍以上），
    // 直接把後端的 anyio 執行緒池與連線池一起打爆。
    //
    // 這個節流器把短時間內的多次刷新合併成一次：已排定就直接併入，未排定則確保
    // 兩次實際請求之間至少間隔 NOTIF_REFETCH_MIN_GAP_MS。
    // 仍保留原本 500ms 的最小延遲（等後端寫入資料庫完成再讀，避免讀到舊資料）。
    const NOTIF_REFETCH_MIN_GAP_MS = 5000;
    const notifRefetchRef = useRef({ timer: null, lastAt: 0 });

    const scheduleNotificationRefetch = useCallback(() => {
        const st = notifRefetchRef.current;
        if (st.timer) return; // 已有排定中的刷新 → 合併，不再多打一次
        const sinceLast = Date.now() - st.lastAt;
        const delay = Math.max(500, NOTIF_REFETCH_MIN_GAP_MS - sinceLast);
        st.timer = setTimeout(() => {
            st.timer = null;
            st.lastAt = Date.now();
            fetchNotifications();
        }, delay);
    }, [fetchNotifications]);

    // 卸載時清掉待執行的計時器，避免元件已卸載還發請求
    useEffect(() => {
        const st = notifRefetchRef.current;
        return () => {
            if (st.timer) {
                clearTimeout(st.timer);
                st.timer = null;
            }
        };
    }, []);

    // Request Notification Permission & Register Token
    // 回傳最終的權限結果，讓呼叫端（教師 PWA 的推播提示）可以立刻依結果調整畫面，
    // 不必等 context 重新渲染才知道使用者到底按了允許還是封鎖。
    const requestNotificationPermission = useCallback(async () => {
        if (!isNotificationSupported) {
            console.log("Notification API not supported on this device/browser.");
            return 'unsupported';
        }

        try {
            const permission = await Notification.requestPermission();
            setPermissionStatus(permission);

            if (permission === 'granted' && messaging) {
                const token = await getToken(messaging, { vapidKey: VAPID_KEY });
                if (token) {
                    console.log("FCM Token:", token);
                    localStorage.setItem('fcm_token', token); // 🆕 Persist for logout cleanup
                    // Register token with backend
                    try {
                        await authAPI.registerDevice(token, 'web');
                    } catch (apiError) {
                        console.error("Failed to register device token:", apiError);
                    }
                }
            }
            return permission;
        } catch (error) {
            console.error("Error requesting permission:", error);
            return 'error';
        }
    }, [user, isNotificationSupported]);

    // 從瀏覽器重新讀一次權限狀態。
    // 為什麼需要：權限可能在 App 之外被改掉（老師照著指引去 iOS 設定裡打開），
    // 那條路徑不會觸發任何 JS 事件，畫面上的提示會一直留著，看起來像沒修好。
    const syncPermissionStatus = useCallback(() => {
        if (!isNotificationSupported) return;
        const current = Notification.permission;
        setPermissionStatus((prev) => (prev === current ? prev : current));

        // 從系統設定補開權限時，這個 session 從來沒跑過註冊流程，
        // 只更新狀態會變成「提示消失了、但其實還是收不到」——比原本更難察覺。
        // 已經是 granted 時 requestPermission() 會直接 resolve，不會再跳提示。
        if (current === 'granted' && !localStorage.getItem('fcm_token')) {
            requestNotificationPermission();
        }
    }, [isNotificationSupported, requestNotificationPermission]);

    // Initial load & Permission Request
    useEffect(() => {
        if (user) {
            fetchNotifications();

            // Auto request permission if supported
            if (isNotificationSupported && Notification.permission !== 'denied') {
                requestNotificationPermission();
            }
        } else {
            setNotifications([]);
            setUnreadCount(0);
        }
    }, [user, fetchNotifications, requestNotificationPermission, isNotificationSupported]);

    const [mountTime] = useState(Date.now());
    const [isBufferActive, setIsBufferActive] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => setIsBufferActive(false), 3000);
        return () => clearTimeout(timer);
    }, []);

    // Helper: Check if notification is too old or during buffer
    const shouldShowToast = useCallback((timestamp) => {
        if (isBufferActive) return false;
        if (!timestamp) return true;

        const msgTime = new Date(timestamp).getTime();
        const now = Date.now();
        // Skip if older than 1 minute (likely a queued/retry message)
        if (now - msgTime > 60000) return false;

        return true;
    }, [isBufferActive]);

    // Listen for Foreground Messages (FCM)
    useEffect(() => {
        if (!user || !isNotificationSupported || !messaging) return;

        let unsubscribe;
        try {
            unsubscribe = onMessage(messaging, (payload) => {
                console.log('[NotificationContext] Foreground message received:', payload);

                // Show Toast only if not too old
                if (shouldShowToast(payload.data?.timestamp)) {
                    toast({
                        title: payload.notification?.title || "新通知",
                        description: payload.notification?.body,
                        status: "info",
                        duration: 5000,
                        isClosable: true,
                        position: "top-right"
                    });
                }

                // Also refresh list to show it in the bell
                fetchNotifications();
            });
        } catch (e) {
            console.warn("FCM onMessage failed (likely unsupported):", e);
        }

        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, [user, toast, fetchNotifications, isNotificationSupported, shouldShowToast]);

    // 🆕 Refetch notifications when App comes to Foreground (Fixes background update issue)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState !== 'visible') return;
            // 回到前景就重新對一次權限：老師去系統設定開完通知再切回來，
            // 是這個提示最常見的「已經修好了」時機。
            syncPermissionStatus();
            if (user) {
                console.log('App foregrounded, refreshing notifications...');
                fetchNotifications();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [user, fetchNotifications, syncPermissionStatus]);

    const [lastDataUpdate, setLastDataUpdate] = useState(Date.now());

    // 🆕 Listen for WebSocket Messages (Backup for FCM + Realtime List Update)
    useEffect(() => {
        if (lastMessage) {
            console.log("WebSocket Broadcast received in NotificationContext:", lastMessage);

            // 1. Refresh notification list (節流；見上方 scheduleNotificationRefetch 的說明)
            //
            // 後端的「Dashboard 靜默更新」與真正的公告共用同一個 type==='broadcast'，
            // 唯一可靠的區別是靜默更新的 message 為空字串（呼叫端傳 message="", audio_url=""）。
            // 靜默更新本來就沒有新通知可讀，卻是尖峰時最高頻的廣播來源，
            // 因此這裡明確排除它——這是砍掉扇出風暴的關鍵一刀。
            // 資料面的即時更新不受影響：那一段走下方的 setLastDataUpdate 訊號。
            const isSilentDataPing = lastMessage.type === 'broadcast' && !lastMessage.message;
            if (!isSilentDataPing &&
                (lastMessage.type === 'broadcast' || lastMessage.type === 'personal_notification')) {
                scheduleNotificationRefetch();
            }

            // 2. 🚀 Broadcast Data Update Signal
            // Triggers for: student_update, roll_call_complete, pickup_request, etc.
            if (['student_update', 'roll_call_complete', 'pickup_request', 'broadcast'].includes(lastMessage.type)) {
                console.log(`[Signal] Broadcasting data update for event: ${lastMessage.type}`);
                setLastDataUpdate(Date.now());
            }

            // Optional: Show Toast if it's a message type
            if (lastMessage.type === 'personal_notification') {
                // Only show toast if not too old
                if (shouldShowToast(lastMessage.timestamp)) {
                    toast({
                        title: "最新消息",
                        description: lastMessage.message,
                        status: "info",
                        duration: 3000,
                        isClosable: true,
                        position: "top-right"
                    });
                }
            }
        }
    }, [lastMessage, scheduleNotificationRefetch, toast, isNotificationSupported, shouldShowToast]);

    // Mark single notification as read
    const markAsRead = async (id) => {
        try {
            // Optimistic update
            setNotifications(prev => prev.map(n =>
                n.id === id ? { ...n, is_read: true } : n
            ));
            setUnreadCount(prev => Math.max(0, prev - 1));

            await authAPI.markNotificationRead(id);
        } catch (error) {
            console.error("Failed to mark notification as read:", error);
        }
    };

    // Mark all as read
    const markAllAsRead = async () => {
        try {
            // Optimistic update
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            setUnreadCount(0);

            await authAPI.markAllNotificationsRead();
        } catch (error) {
            console.error("Failed to mark all as read:", error);
        }
    };

    // Add new notification (for WebSocket integration legacy)
    const addNotification = (newNotification) => {
        setNotifications(prev => [newNotification, ...prev]);
        setUnreadCount(prev => prev + 1);
    };

    const value = {
        notifications,
        unreadCount,
        isLoading,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        addNotification,
        requestNotificationPermission,
        // 權限狀態一直存在 state 裡卻沒有對外輸出，等於沒有任何畫面能知道推播是壞的——
        // 這正是「推播失效完全無聲」的成因之一。輸出後由 PushHealthBanner 顯示。
        permissionStatus,
        isNotificationSupported,
        syncPermissionStatus,
        lastDataUpdate
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};
